import {
  UnstyledButton,
  Menu,
  Image,
  rem,
  Button,
  Group,
  Stack,
  createStyles,
  Paper,
  Tabs,
  Title,
  Center,
  Text,
  PasswordInput,
  TextInput,
} from "@mantine/core";
import {
  IconPassword,
  IconUser,
  IconChevronDown,
  IconAdjustments,
} from "@tabler/icons-react";
import React, { useState } from "react";
import UpdatePasswordForm from "../../Authentication/UpdatePasswordForm";
import { useForm } from "@mantine/form";
import fetchApi from "../../Lib/api";
import Notification from "../../Lib/Notification";

import LanguageDropdown from "../../Lib/Language";
import { useTranslation } from "react-i18next";
import useUserStore from "../../../stores/userStore";
// General Settings Component for Tab Body
const GeneralSettings = ({ ai, digest, projectUpdates }) => {
  const [enableAi, setEnableAi] = useState(ai)
  const { t } = useTranslation();

  const handleAiFeatureChange = async (isEnabled) => {
    setEnableAi(isEnabled)
    let url;
    let method;

    if (isEnabled) {
      url = "/api/users/enable_ai";
      method = "POST";
    } else {
      url = "/api/users/disable_ai";
      method = "POST";
    }

    try {
      const response = await fetchApi(url, method);

      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      } else {
        const errorData = await response
          .json()
          .catch(() => ({ message: "No error details provided." }));
        throw new Error(
          `Failed to update AI feature: ${response.status} - ${JSON.stringify(
            errorData
          )}`
        );
      }
    } catch (error) {
      throw error;
    }
  };

  // Daily digest toggle
  const [dailyDigestEnabled, setDailyDigestEnabled] = useState(digest);
  const handleDailyDigestChange = async (isEnabled) => {
    setDailyDigestEnabled(isEnabled);
    const url = isEnabled ? "/api/users/enable_daily_digest" : "/api/users/disable_daily_digest";
    try {
      const response = await fetchApi(url, "POST");
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to update daily digest: ${response.status} - ${JSON.stringify(errorData)}`);
      }
      return { success: true };
    } catch (e) {
      // revert on failure
      setDailyDigestEnabled(!isEnabled);
      throw e;
    }
  };

  // Project updates toggle
  const [projectUpdatesEnabled, setProjectUpdatesEnabled] = useState(projectUpdates);
  const handleProjectUpdatesChange = async (isEnabled) => {
    setProjectUpdatesEnabled(isEnabled);
    const url = isEnabled ? "/api/users/enable_project_updates" : "/api/users/disable_project_updates";
    try {
      const response = await fetchApi(url, "POST");
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to update project updates: ${response.status} - ${JSON.stringify(errorData)}`);
      }
      return { success: true };
    } catch (e) {
      // revert on failure
      setProjectUpdatesEnabled(!isEnabled);
      throw e;
    }
  };

  return (
    <div className="">
      {/* Billing Section */}
      <PreferenceSection
        title={"Billings and Credits"}
        description={"Manage your billing details and credit usage"}
      >
        <PreferenceItem
          title={"Subscription & Usage Details"}
          description={
            "Check your active plan and monitor your usage statistics"
          }
          type="billingButton"
        />
      </PreferenceSection>

      {/* AI Settings Section */}
      <PreferenceSection
        title={t("settings.ai_settings")}
        description={t("settings.ai_settings_subheading")}
      >
        <PreferenceItem
          title={t("settings.enable_features")}
          description={t("settings.enable_features_subheading")}
          type="switch"
          value={enableAi}
          onChange={(value) => handleAiFeatureChange(value)}
        />
      </PreferenceSection>

      {/* Email Settings Section */}
      <PreferenceSection
        title={"Email Notifications"}
        description={"Manage your email notification preferences"}
      >
        <PreferenceItem
          title={"Daily project reminder"}
          description={"Receive a daily summary email for your projects"}
          type="switch"
          value={dailyDigestEnabled}
          onChange={(value) => handleDailyDigestChange(value)}
        />
        <PreferenceItem
          title={"Project update notifications"}
          description={"Get notified when team members update project tasks"}
          type="switch"
          value={projectUpdatesEnabled}
          onChange={(value) => handleProjectUpdatesChange(value)}
        />
      </PreferenceSection>

      {/* Privacy Settings Section */}
      <PreferenceSection
        title={t("settings.privacy")}
        description={t("settings.privacy_subheading")}
      >
        <PreferenceItem
          title={t("settings.terms")}
          description={t("settings.terms_subheading")}
          type="button"
          buttonText="Refresh Terms & Privacy"
        />
      </PreferenceSection>

      {/* Language Settings Section */}
      <PreferenceSection
        title={t("settings.language_settings")}
        description={t("settings.language_subheading")}
      >
        <PreferenceItem
          title={t("settings.language")}
          description={t("settings.language_preffered")}
          type="select"
        />
      </PreferenceSection>
    </div>
  );
};

// Enhanced Section Component
const PreferenceSection = ({ title, description, children }) => {
  return (
    <div className="space-y-6 mt-8">
      <div className="border-l-4 border-blue-500 pl-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        )}
      </div>
      <div className="space-y-6 ml-4">{children}</div>
    </div>
  );
};

// Enhanced Preference Item
const PreferenceItem = ({
  title,
  description,
  type,
  value,
  onChange,
  badge = null,
  badgeColor = "blue",
}) => {
  const renderInput = () => {
    switch (type) {
      case "switch":
        return <PreferenceSwitch checked={value} onChange={onChange} />;

      case "select":
        return <LanguageDropdown />;

      case "button":
        return <TermlyConsentTriggerButton />;

      case "billingButton":
        return <BillingButton />;

      default:
        return null;
    }
  };

  const badgeColors = {
    blue: "bg-blue-100 text-blue-800",
    green: "bg-green-100 text-green-800",
    purple: "bg-purple-100 text-purple-800",
    amber: "bg-amber-100 text-amber-800",
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0 pr-4">
          <div className="flex items-center space-x-2">
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
            {badge && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeColors[badgeColor]}`}
              >
                {badge}
              </span>
            )}
          </div>
          {description && (
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              {description}
            </p>
          )}
        </div>
        <div className="flex-shrink-0">{renderInput()}</div>
      </div>
    </div>
  );
};

// Enhanced Switch with better styling
const PreferenceSwitch = ({ checked, onChange }) => {
  return (
    <button
      type="button"
      className={`${
        checked
          ? "bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25"
          : "bg-gray-200 hover:bg-gray-300"
      } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105`}
      onClick={() => onChange(!checked)}
    >
      <span
        className={`${
          checked ? "translate-x-5" : "translate-x-0"
        } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition-all duration-300 ease-in-out`}
      />
    </button>
  );
};

const TermlyConsentTriggerButton = () => {
  const [isProcessing, setIsProcessing] = useState(false);

  const clearAndReload = () => {
    setIsProcessing(true);

    // Show user feedback with a nice notification
    const showNotification = (message, type = "info") => {
      const notification = document.createElement("div");
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === "info" ? "#007bff" : "#28a745"};
        color: white;
        padding: 16px 24px;
        border-radius: 8px;
        box-shadow: 0 4px 16px rgba(0,0,0,0.15);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        font-weight: 500;
        max-width: 320px;
        animation: slideIn 0.3s ease-out;
      `;

      // Add slide-in animation
      const style = document.createElement("style");
      style.textContent = `
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `;
      document.head.appendChild(style);

      notification.textContent = message;
      document.body.appendChild(notification);

      return notification;
    };

    try {
      // Show initial feedback
      const notification = showNotification("Clearing cookie preferences...");

      // Clear localStorage items
      localStorage.removeItem("TERMLY_API_CACHE");
      localStorage.removeItem("__tltpl_7ae9c89a-ba3f-4330-8b2c-e8a2d07e0499");

      // Clear cookies - be thorough
      const cookiesToClear = [
        "TERMLY_VIEWED",
        "TERMLY_CONSENT",
        "TERMLY_API_CACHE",
        "__tltpl_7ae9c89a-ba3f-4330-8b2c-e8a2d07e0499",
      ];

      cookiesToClear.forEach((cookieName) => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
        // Also try with leading dot for subdomain cookies
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
      });

      // Update notification and reload
      setTimeout(() => {
        notification.textContent = "Preferences cleared! Reloading page...";
        notification.style.background = "#28a745";

        setTimeout(() => {
          window.location.reload();
        }, 800);
      }, 600);
    } catch (error) {
      console.error("Error clearing preferences:", error);
      setIsProcessing(false);

      // Show error notification
      showNotification(
        "Error clearing preferences. Please try again.",
        "error"
      );
    }
  };

  const baseClasses =
    "px-4 py-2 text-sm font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
  const primary =
    "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-blue-500 shadow-sm hover:shadow-md";
    const {t}=useTranslation();

  return (
    <div style={{ marginTop: "20px" }}>
      <button
        onClick={clearAndReload}
        type="button"
        disabled={isProcessing}
        className={`${baseClasses} ${primary}`}
      >
        {isProcessing ? t("settings.updating_cookie_preferences"):t("settings.cookie_preferences")}
      </button>
    </div>
  );
};


const BillingButton = () => {
  const baseClasses =
  "px-4 py-2 text-sm font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
const primary =
  "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-blue-500 shadow-sm hover:shadow-md";
  return (
    <div style={{ marginTop: "20px" }}>
      <button
        onClick={() => {
          window.location.href = "/billings";
        }}
        className={`${baseClasses} ${primary}`}
      >
        Manage Billing
      </button>
    </div>
  );
};

const useStyles = createStyles((theme) => ({
  control: {
    width: rem(200),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: [theme.spacing.xs, theme.spacing.md],
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[4]}`,
    transition: "background-color 150ms ease",
    backgroundColor: theme.colors.gray[0],
    "&[data-expanded]": {
      backgroundColor: theme.colors.gray[0],
    },
    "&:hover": {
      backgroundColor: theme.colors.gray[0],
    },
  },
  label: {
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
    marginRight: theme.spacing.md,
  },
  icon: {
    transition: "transform 150ms ease",
    transform: "rotate(0deg)",
    "[data-expanded] &": {
      transform: "rotate(180deg)",
    },
  },
}));

export function DropDownSelect({}) {
  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState(data[0]);
  const { classes, cx } = useStyles();
  const items = data.map((item) => (
    <Menu.Item
      icon={<Image src={item.image} width={18} height={18} />}
      onClick={() => setSelected(item)}
      key={item.label}
    >
      {item.label}
    </Menu.Item>
  ));

  return (
    <Menu
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      radius="md"
      width="target"
      withinPortal
    >
      <Menu.Target>
        <UnstyledButton
          className={classes.control}
          data-expanded={opened || undefined}
        >
          <Group gap="xs">
            {/* <Image src={selected.image} width={22} height={22} /> */}
            <span className={classes.label}>{selected.label}</span>
          </Group>
          <IconChevronDown size="1rem" className={classes.icon} stroke={1.5} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>{items}</Menu.Dropdown>
    </Menu>
  );
}

export function ChangeEmail() {
  const [notification, setNotification] = useState(null);
  const { t } = useTranslation();
  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      password: (val) =>
        val.length >= 6
          ? null
          : "Password should include at least 6 characters",
      email: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
    },
  });

  const handleSubmit = async (e) => {
    try {
      const response = await fetchApi("/api/users/update_email", "PUT", {
        user: { password: form.values.password, email: form.values.email },
      });

      if (response.ok) {
        const msg = (await response.json()).message;
        setNotification({ type: "success", content: msg });
        console.log("Email change successful!");
      } else {
        const msg = (await response.json()).error;
        setNotification({ type: "failure", content: msg });
        console.error("Email change failed.");
      }
    } catch (error) {
      console.error("Error during email change:", error);
    }
  };

  return (
    <form
      onSubmit={form.onSubmit((e) => handleSubmit())}
      className="mx-auto lg:w-2/5 lg:mt-5"
    >
      <Stack p={0}>
        {notification && <Notification {...notification} />}
        <TextInput
          required
          label={t("settings.new_email")}
          placeholder={t("settings.your_new_email")}
          value={form.values.email}
          onChange={(event) =>
            form.setFieldValue("email", event.currentTarget.value)
          }
          error={form.errors.email}
          radius="md"
          size="md"
        />
        <PasswordInput
          required
          label={t("settings.password")}
          placeholder={t("settings.your_password")}
          value={form.values.password}
          onChange={(event) =>
            form.setFieldValue("password", event.currentTarget.value)
          }
          error={form.errors.password}
          radius="md"
          size="md"
        />
        <Center>
          <Button
            type="submit"
            variant="default"
            fullWidth
            className="w-fit lg:px-12"
          >
            {t("settings.change_email")}
          </Button>
        </Center>
      </Stack>
    </form>
  );
}

function DeleteAccount() {
  const [notification, setNotification] = useState(null);
  const { t } = useTranslation();
  const form = useForm({
    initialValues: {
      password: "",
    },
    validate: {
      password: (val) =>
        val.length >= 6
          ? null
          : "Password should include at least 6 characters",
    },
  });
  const handleSubmit = async (e) => {
    try {
      const response = await fetchApi("/api/users/delete_account", "DELETE", {
        user: { password: form.values.password },
      });

      if (response.ok) {
        const msg = (await response.json()).message;
        setNotification({ type: "success", content: msg });
        window.location.href = "/";
        console.log("Account Deleted successful!");
      } else {
        const msg = (await response.json()).error;
        setNotification({ type: "failure", content: msg });
        console.error("Account Deletion failed.");
      }
    } catch (error) {
      console.error("Error during password change:", error);
    }
  };
  return (
    <form
      onSubmit={form.onSubmit((e) => handleSubmit())}
      className="mx-auto lg:w-2/5 lg:mt-5"
    >
      <Stack>
        <Text className="mt-2 px-2 text-base text-gray-600">
          {t("settings.confirm")}
        </Text>
        <Stack className="">
          <PasswordInput
            required
            label={t("settings.password")}
            placeholder={t("settings.your_password")}
            value={form.values.password}
            onChange={(event) =>
              form.setFieldValue("password", event.currentTarget.value)
            }
            error={form.errors.password}
            radius="md"
            size="md"
            className="w-full"
          />
          <Button
            type="submit"
            variant="default"
            fullWidth
            className="w-fit mx-auto lg:px-12 text-red-700"
          >
            {t("settings.delete_account")}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}

export default function Settings() {
  const [firstTab, setFirstTab] = useState("preferences");
  const { t } = useTranslation();
  const {
    user: { enable_ai: ai, daily_digest_enabled, project_update_notifications_enabled },
  } = useUserStore((state) => state.user);

  return (
    <main className="flex justify-center ">
      <div className="max-w-5xl w-full lg:p-6">
        <Tabs
          color="lime"
          radius="md"
          defaultValue="personal"
          onTabChange={setFirstTab}
          value={firstTab}
        >
          <Tabs.List grow>
            <Tabs.Tab
              value="preferences"
              icon={<IconAdjustments size="1.5rem" />}
            >
             {t("settings.preferences")}
            </Tabs.Tab>
            <Tabs.Tab value="password" icon={<IconPassword size="1.5rem" />}>
              {t("settings.password")}
            </Tabs.Tab>
            <Tabs.Tab value="account" icon={<IconUser size="1.5rem" />}>
              {t("settings.account")}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="preferences" pt="xs">
            <Stack p="xl" spacing={10}>
              <Paper shadow="xs" radius="md" withBorder p="md" className="">
                <GeneralSettings ai={ai} digest={daily_digest_enabled} projectUpdates={project_update_notifications_enabled} />
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="password" pt="xs">
            <Stack p="xl" spacing={10}>
              <Title order={5} className="text-gray-600">
                {t("settings.password_change")}
              </Title>
              <Paper shadow="xs" radius="md" withBorder p="xl" className="">
                <UpdatePasswordForm />
              </Paper>
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="account" pt="xs">
            <Stack p="xl" spacing={10}>
              <Title order={5} className="text-gray-600">
                {t("settings.change_email")}
              </Title>
              <Paper shadow="xs" radius="md" withBorder p="xl" className="">
                <ChangeEmail />
              </Paper>
              <Title order={5} className="text-gray-600">
                {t("settings.delete_account")}
              </Title>
              <Paper
                shadow="xs"
                radius="md"
                withBorder
                p="xl"
                className="border-red-500"
              >
                <DeleteAccount />
              </Paper>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </div>
    </main>
  );
}
